import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getPaymentEnvironment } from "@/lib/stripe";
import { fetchPremiumState, type PremiumState } from "@/lib/premium";
import { trackRokuSignup } from "@/lib/roku";
import { recordConfirmedRegistration } from "@/lib/study-conversion.functions";
import { getFunnelAttribution } from "@/lib/study-conversion";
import { getStudyUsage } from "@/lib/study-monetization.functions";
import type { StudyUsage } from "@/lib/study-policy";

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isPaid: boolean;
  scanCount: number;
  scanLimit: number;
  streak: number;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  planPriceId: string | null;
  /** Authoritative server-computed premium/trial state. */
  premium: PremiumState | null;
  questionUsage?: StudyUsage | null;
}

interface AuthContextType extends AuthState {
  signUp: (email: string, password: string) => Promise<{ error: string | null; hasSession: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null; hasSession: boolean }>;
  signOut: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  refreshPremium: () => Promise<void>;
  applyServerUsage: (usage: { count: number; limit: number } & Partial<StudyUsage>) => void;
  applyStreak: (streak: number) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const FREE_SCAN_LIMIT = 3;
const PAYMENT_ENV = getPaymentEnvironment();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const activeFetchRef = useRef(0);
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isPaid: false,
    scanCount: 0,
    scanLimit: FREE_SCAN_LIMIT,
    streak: 0,
    subscriptionStatus: null,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
    planPriceId: null,
    premium: null,
  });

  const fetchUserData = useCallback(async (userId: string) => {
    const requestId = ++activeFetchRef.current;
    try {
      // One parallel round trip instead of five sequential ones: the newest
      // subscription row, today's usage, the profile, and the authoritative
      // server-side premium state.
      const today = new Date().toISOString().split("T")[0];
      const [{ data: sub }, { data: usage }, { data: profile }, premium, questionUsage] = await Promise.all([
        supabase
          .from("subscriptions")
          .select("status, cancel_at_period_end, current_period_end, price_id")
          .eq("user_id", userId)
          .eq("environment", PAYMENT_ENV)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("scan_usage")
          .select("scan_count")
          .eq("user_id", userId)
          .eq("scan_date", today)
          .maybeSingle(),
        supabase
          .from("profiles")
          .select("streak_count, pro_bonus_until")
          .eq("user_id", userId)
          .maybeSingle(),
        fetchPremiumState(),
        getStudyUsage().catch(() => null),
      ]);

    let isPaid = false;
    let subscriptionStatus: string | null = null;
    let cancelAtPeriodEnd = false;
    let currentPeriodEnd: string | null = null;
    let planPriceId: string | null = null;

    if (sub && ["active", "trialing", "past_due"].includes(sub.status)) {
      // past_due keeps access during Stripe's retry (dunning) window.
      isPaid = true;
      subscriptionStatus = sub.status;
      cancelAtPeriodEnd = sub.cancel_at_period_end ?? false;
      currentPeriodEnd = sub.current_period_end;
      planPriceId = sub.price_id ?? null;
    } else if (
      sub?.status === "canceled" &&
      sub.current_period_end &&
      new Date(sub.current_period_end) > new Date()
    ) {
      // Canceled but still inside the paid period.
      isPaid = true;
      subscriptionStatus = "canceled";
      cancelAtPeriodEnd = true;
      currentPeriodEnd = sub.current_period_end;
      planPriceId = sub.price_id ?? null;
    }

    // Referral bonus grants Pro access even without a paid subscription
    const bonusUntil = (profile as { pro_bonus_until?: string | null } | null)?.pro_bonus_until ?? null;
    if (!isPaid && bonusUntil && new Date(bonusUntil) > new Date()) {
      isPaid = true;
      subscriptionStatus = subscriptionStatus ?? "referral_bonus";
      currentPeriodEnd = bonusUntil;
    }

    // The server decides entitlement (trial, grace period, subscription) — trust it over local inference.
    if (premium.authenticated) {
      isPaid = premium.premium_access || isPaid;
      subscriptionStatus =
        premium.trial_status === "active" ? "trialing" : (premium.subscription_status ?? subscriptionStatus);
      cancelAtPeriodEnd = premium.cancel_at_period_end || cancelAtPeriodEnd;
      currentPeriodEnd =
        premium.trial_status === "active"
          ? (premium.trial_ends_at ?? currentPeriodEnd)
          : (premium.current_period_end ?? premium.subscription_expires_at ?? currentPeriodEnd);
    }

      if (requestId !== activeFetchRef.current) return;
      setState((prev) => {
        if (prev.user?.id !== userId) return prev;
        return {
          ...prev,
          isPaid,
          premium,
          questionUsage,
          subscriptionStatus,
          cancelAtPeriodEnd,
          currentPeriodEnd,
          planPriceId,
          scanCount: questionUsage?.enforcementEnabled ? questionUsage.count : usage?.scan_count ?? 0,
          scanLimit: questionUsage?.enforcementEnabled ? (questionUsage.limit === -1 ? Infinity : questionUsage.limit) : isPaid ? Infinity : FREE_SCAN_LIMIT,
          streak: profile?.streak_count ?? 0,
        };
      });
    } catch (error) {
      if (requestId === activeFetchRef.current) {
        console.error("Failed to refresh account data", error);
      }
    }
  }, []);

  useEffect(() => {
    let lastLoadedUserId: string | null = null;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setState((prev) => ({
          ...prev,
          session,
          user: session?.user ?? null,
          isLoading: false,
          ...(!session
            ? {
                isPaid: false,
                scanCount: 0,
                scanLimit: FREE_SCAN_LIMIT,
                streak: 0,
                subscriptionStatus: null,
                cancelAtPeriodEnd: false,
                currentPeriodEnd: null,
                planPriceId: null,
                premium: null,
                questionUsage: null,
              }
            : {}),
        }));
        if (session?.user) {
          // A token refresh for the same user changes nothing we fetch here.
          const sameUser = lastLoadedUserId === session.user.id;
          if (event === "TOKEN_REFRESHED" && sameUser) return;
          lastLoadedUserId = session.user.id;
          // Do not request another auth session from inside the auth callback lock.
          if (event === "SIGNED_IN") setTimeout(() => {
            void recordConfirmedRegistration({ data: { attribution: getFunnelAttribution() } })
              .then((r) => {
                // Ad-network signup conversion fires only when the server
                // recorded the one canonical confirmed registration for this
                // account — never on unconfirmed signups or returning logins.
                if (r?.recorded) {
                  const provider = session.user.app_metadata?.provider;
                  trackRokuSignup(provider === "apple" ? "apple" : provider === "google" ? "google" : "email");
                }
              })
              .catch(() => {});
          }, 0);
          setTimeout(() => fetchUserData(session.user.id), 0);
        } else {
          lastLoadedUserId = null;
          activeFetchRef.current++;
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        isLoading: false,
      }));
      if (session?.user) {
        lastLoadedUserId = session.user.id;
        fetchUserData(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData]);

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    // No conversion pixel here: with email confirmation enabled the account is
    // not confirmed yet. The Roku SIGN_UP fires from the SIGNED_IN handler
    // above, only when the server records the confirmed registration.
    return { error: error?.message ?? null, hasSession: Boolean(data.session) };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null, hasSession: !error };
  };

  const signOut = async () => {
    activeFetchRef.current++;
    await supabase.auth.signOut();
    setState({
      user: null,
      session: null,
      isLoading: false,
      isPaid: false,
      scanCount: 0,
      scanLimit: FREE_SCAN_LIMIT,
      streak: 0,
      subscriptionStatus: null,
      cancelAtPeriodEnd: false,
      currentPeriodEnd: null,
      planPriceId: null,
      premium: null,
      questionUsage: null,
    });
  };

  const refreshUsage = useCallback(async () => {
    if (state.user) {
      await fetchUserData(state.user.id);
    }
  }, [state.user, fetchUserData]);

  const refreshPremium = useCallback(async () => {
    if (state.user) await fetchUserData(state.user.id);
  }, [fetchUserData, state.user]);

  useEffect(() => {
    const userId = state.user?.id;
    if (!userId) return;
    const refresh = () => { void fetchUserData(userId); };
    const timer = setInterval(refresh, 60000);
    const resetDelay = state.questionUsage?.resetAt ? Math.max(1, new Date(state.questionUsage.resetAt).getTime() - Date.now() + 100) : null;
    const resetTimer = resetDelay !== null ? setTimeout(refresh, Math.min(resetDelay, 86400100)) : null;
    window.addEventListener("focus", refresh);
    return () => { clearInterval(timer); if (resetTimer !== null) clearTimeout(resetTimer); window.removeEventListener("focus", refresh); };
  }, [state.user, state.questionUsage?.resetAt, fetchUserData]);

  const applyServerUsage = useCallback((usage: { count: number; limit: number } & Partial<StudyUsage>) => {
    if (!usage || typeof usage.count !== "number") return;
    setState((prev) => ({
      ...prev,
      scanCount: usage.count,
      scanLimit: usage.limit === -1 ? Infinity : usage.limit,
      questionUsage: prev.questionUsage ? { ...prev.questionUsage, ...usage } : prev.questionUsage,
    }));
  }, []);

  const applyStreak = useCallback((streak: number) => {
    if (typeof streak !== "number") return;
    setState((prev) => ({ ...prev, streak }));
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, signUp, signIn, signOut, refreshUsage, refreshPremium, applyServerUsage, applyStreak }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
